import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SpellcheckInput from "@/components/SpellcheckInput";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { isDespachoReadOnly } from "@/modules/despacho/despachoPermissions";
import { useDespachoWorkspaceAuxiliaries } from "@/modules/despacho/useDespachoWorkspaceAuxiliaries";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

const WEEKDAYS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

const RECURRENCE_OPTIONS = [
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensual" },
  { value: "biweekly", label: "Quincenal" },
  { value: "annual", label: "Anual" },
];

/** Valor enviado al API cuando el recordatorio va a todos los auxiliares del despacho. */
export const OPERATIONAL_RECIPIENT_ALL_AUXILIARIES = "__all_auxiliaries__";

const EMPTY_TEMPLATE = {
  label: "",
  message_body: "",
  recipient_user_id: "",
  recurrence_mode: "monthly",
  weekday: 3,
  day_of_month: 13,
  month: 1,
  days_before_month_end: 2,
  reminder_offsets: [0],
  active: true,
};

function normalizeOffsetList(raw) {
  if (!Array.isArray(raw) || !raw.length) return [0];
  const nums = raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
  if (!nums.length) return [0];
  return [...new Set(nums)].sort((a, b) => a - b);
}

function clampOffset(value, { min = -90, max = 90 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function offsetHelpText(days) {
  if (days < 0) return `${Math.abs(days)} días antes de la fecha base`;
  if (days === 0) return "Día de la fecha base";
  return `${days} día${days === 1 ? "" : "s"} después de la fecha base`;
}

function OperationalOffsetEditor({ reminderOffsets, readOnly, onChange }) {
  const offsets = normalizeOffsetList(reminderOffsets);

  const update = (next) => onChange({ reminder_offsets: next });

  return (
    <div className="space-y-2 rounded-sm border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium">Cuándo enviar (respecto a la fecha base)</Label>
        {!readOnly ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm h-7 text-xs"
            onClick={() => update([...offsets, offsets.length ? offsets[offsets.length - 1] + 1 : -2])}
          >
            <Plus className="h-3 w-3 mr-1" aria-hidden />
            Agregar
          </Button>
        ) : null}
      </div>
      <p className="text-[11px] text-slate-500 m-0 leading-snug">
        Negativo = antes, 0 = el mismo día, positivo = después. Si cae en inhábil, se ajusta al día hábil.
      </p>
      <ul className="space-y-2 m-0 p-0 list-none">
        {offsets.map((offset, idx) => (
          <li key={`op-off-${idx}`} className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min={-90}
              max={90}
              className="flex h-8 w-20 rounded-sm border border-slate-200 px-2 text-sm bg-white"
              value={offset}
              disabled={readOnly}
              onChange={(e) => {
                const next = [...offsets];
                next[idx] = clampOffset(e.target.value);
                update([...new Set(next)].sort((a, b) => a - b));
              }}
            />
            <span className="text-xs text-slate-600">{offsetHelpText(offset)}</span>
            {!readOnly && offsets.length > 1 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-rose-700"
                onClick={() => update(offsets.filter((_, i) => i !== idx))}
              >
                Quitar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function recurrenceSummary(tpl) {
  const mode = tpl.recurrence_mode || "monthly";
  if (mode === "weekly") {
    const wd = WEEKDAYS.find((w) => w.value === Number(tpl.weekday))?.label || "—";
    return `Cada ${wd}`;
  }
  if (mode === "monthly") {
    return `Día ${tpl.day_of_month || 1} de cada mes`;
  }
  if (mode === "biweekly") {
    const end = tpl.days_before_month_end != null ? ` y ${tpl.days_before_month_end} días antes del fin de mes` : "";
    return `Día ${tpl.day_of_month || 13} del mes${end}`;
  }
  if (mode === "annual") {
    const mo = MONTHS.find((m) => m.value === Number(tpl.month))?.label || "—";
    return `${tpl.day_of_month || 1} de ${mo}`;
  }
  return mode;
}

function recipientDisplayName(tpl) {
  if (
    tpl.recipient_all_auxiliaries
    || tpl.recipient_user_id === OPERATIONAL_RECIPIENT_ALL_AUXILIARIES
  ) {
    return "Todos los auxiliares";
  }
  return tpl.recipient_name || tpl.recipient_user_id || "—";
}

function AccordionSectionCard({ title, description, isOpen, onToggle, children, testId }) {
  return (
    <section
      className="rounded-sm border p-0 space-y-0 border-slate-200 bg-white"
      data-testid={testId}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span className="min-w-0">
          <span className="block text-sm font-bold text-[#0F2D5C] truncate">{title}</span>
          {description ? (
            <span className="block text-xs text-slate-500 mt-1 line-clamp-2">{description}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {isOpen ? (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {children}
        </div>
      ) : null}
    </section>
  );
}

function TemplateEditor({ template, auxiliares, readOnly, saving, deleting, onChange, onSave, onDelete }) {
  const isNew = !template.id;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1 sm:col-span-2">
        <Label className="text-xs">Auxiliar destinatario</Label>
        <select
          className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
          value={
            template.recipient_all_auxiliaries
            || template.recipient_user_id === OPERATIONAL_RECIPIENT_ALL_AUXILIARIES
              ? OPERATIONAL_RECIPIENT_ALL_AUXILIARIES
              : (template.recipient_user_id || "")
          }
          disabled={readOnly}
          onChange={(e) => onChange({
            recipient_user_id: e.target.value,
            recipient_all_auxiliaries: e.target.value === OPERATIONAL_RECIPIENT_ALL_AUXILIARIES,
          })}
        >
          <option value="">Seleccione auxiliar…</option>
          <option value={OPERATIONAL_RECIPIENT_ALL_AUXILIARIES}>Todos los auxiliares</option>
          {auxiliares.map((aux) => (
            <option key={aux.id} value={aux.id}>
              {aux.nombre || aux.email || aux.id}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <SpellcheckInput
          label="Nombre del recordatorio"
          labelClassName="text-xs"
          className="rounded-sm h-9"
          value={template.label || ""}
          disabled={readOnly}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Ej. Revisión de nóminas"
          testId="operational-reminder-label"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Periodicidad</Label>
        <select
          className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
          value={template.recurrence_mode || "monthly"}
          disabled={readOnly}
          onChange={(e) => onChange({ recurrence_mode: e.target.value })}
        >
          {RECURRENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {template.recurrence_mode === "weekly" ? (
        <div className="space-y-1">
          <Label className="text-xs">Día de la semana</Label>
          <select
            className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
            value={Number(template.weekday ?? 3)}
            disabled={readOnly}
            onChange={(e) => onChange({ weekday: Number(e.target.value) })}
          >
            {WEEKDAYS.map((wd) => (
              <option key={wd.value} value={wd.value}>
                {wd.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {template.recurrence_mode === "monthly" || template.recurrence_mode === "biweekly" ? (
        <div className="space-y-1">
          <Label className="text-xs">Día del mes</Label>
          <input
            type="number"
            min={1}
            max={31}
            className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
            value={template.day_of_month ?? 13}
            disabled={readOnly}
            onChange={(e) => onChange({ day_of_month: Number(e.target.value) })}
          />
        </div>
      ) : null}

      {template.recurrence_mode === "biweekly" ? (
        <div className="space-y-1">
          <Label className="text-xs">Segunda fecha (días antes del fin de mes)</Label>
          <input
            type="number"
            min={0}
            max={28}
            className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
            value={template.days_before_month_end ?? 2}
            disabled={readOnly}
            onChange={(e) => onChange({ days_before_month_end: Number(e.target.value) })}
          />
        </div>
      ) : null}

      {template.recurrence_mode === "annual" ? (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Mes</Label>
            <select
              className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
              value={Number(template.month ?? 1)}
              disabled={readOnly}
              onChange={(e) => onChange({ month: Number(e.target.value) })}
            >
              {MONTHS.map((mo) => (
                <option key={mo.value} value={mo.value}>
                  {mo.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Día</Label>
            <input
              type="number"
              min={1}
              max={31}
              className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
              value={template.day_of_month ?? 1}
              disabled={readOnly}
              onChange={(e) => onChange({ day_of_month: Number(e.target.value) })}
            />
          </div>
        </>
      ) : null}

      <SpellcheckTextarea
        label="Mensaje a enviar"
        labelClassName="text-xs"
        wrapperClassName="sm:col-span-2"
        className="rounded-sm min-h-[80px]"
        value={template.message_body || ""}
        disabled={readOnly}
        onChange={(e) => onChange({ message_body: e.target.value })}
        placeholder="Texto que verá el auxiliar en el chat interno."
        testId="operational-reminder-message"
      />

      <div className="sm:col-span-2">
        <OperationalOffsetEditor
          reminderOffsets={template.reminder_offsets}
          readOnly={readOnly}
          onChange={onChange}
        />
      </div>

      <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={template.active !== false}
            disabled={readOnly}
            onChange={(e) => onChange({ active: e.target.checked })}
          />
          Activo
        </label>
        {!readOnly ? (
          <>
            <Button
              type="button"
              size="sm"
              className="rounded-sm"
              disabled={saving}
              onClick={() => onSave(template)}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {isNew ? "Crear recordatorio" : "Guardar cambios"}
            </Button>
            {!isNew ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-rose-700 border-rose-200"
                disabled={deleting}
                onClick={() => onDelete(template)}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
                Eliminar
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function CalendarioOperationalRemindersPanel() {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });
  const { auxiliares, loading: auxLoading } = useDespachoWorkspaceAuxiliaries();

  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [draftNew, setDraftNew] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [openTemplateId, setOpenTemplateId] = useState("");

  const loadItems = useCallback(async () => {
    setLoadState("loading");
    try {
      const rows = await despachoFiscalInfoApi.listOperationalReminderTemplates();
      setItems(rows);
      setLoadState("ready");
    } catch (err) {
      setItems([]);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const displayItems = useMemo(() => {
    return items.map((item) => ({
      ...item,
      ...(edits[item.id] || {}),
    }));
  }, [edits, items]);

  const patchEdit = (id, patch) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  };

  const saveTemplate = async (tpl) => {
    if (readOnly) return;
    const saveKey = tpl.id || "__new__";
    if (savingId) return;
    const allAux =
      tpl.recipient_all_auxiliaries === true
      || tpl.recipient_user_id === OPERATIONAL_RECIPIENT_ALL_AUXILIARIES;
    const recipientUserId = allAux
      ? OPERATIONAL_RECIPIENT_ALL_AUXILIARIES
      : (tpl.recipient_user_id || "").trim();
    const label = (tpl.label || "").trim();
    const messageBody = (tpl.message_body || "").trim();
    if (!recipientUserId) {
      toast.error("Seleccione un auxiliar destinatario.");
      return;
    }
    if (!label) {
      toast.error("Indique un nombre para el recordatorio.");
      return;
    }
    if (!messageBody) {
      toast.error("Indique el mensaje a enviar.");
      return;
    }
    const body = {
      label,
      message_body: messageBody,
      recipient_user_id: recipientUserId,
      recipient_all_auxiliaries: allAux,
      recurrence_mode: tpl.recurrence_mode || "monthly",
      weekday: Number(tpl.weekday ?? 3),
      day_of_month: Number(tpl.day_of_month ?? 13),
      month: Number(tpl.month ?? 1),
      days_before_month_end:
        tpl.recurrence_mode === "biweekly" ? Number(tpl.days_before_month_end ?? 2) : null,
      reminder_offsets: normalizeOffsetList(tpl.reminder_offsets),
      active: tpl.active !== false,
    };
    setSavingId(saveKey);
    try {
      if (tpl.id) {
        await despachoFiscalInfoApi.updateOperationalReminderTemplate(tpl.id, body);
        toast.success("Recordatorio actualizado.");
      } else {
        const result = await despachoFiscalInfoApi.createOperationalReminderTemplate(body);
        toast.success("Recordatorio creado.");
        setDraftNew(null);
        if (result?.item?.id) {
          setOpenTemplateId(result.item.id);
        }
      }
      setEdits({});
      await loadItems();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setSavingId("");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (readOnly || !tpl.id) return;
    if (!window.confirm(`¿Eliminar el recordatorio «${tpl.label || tpl.id}»?`)) return;
    setDeletingId(tpl.id);
    try {
      await despachoFiscalInfoApi.deleteOperationalReminderTemplate(tpl.id);
      toast.success("Recordatorio eliminado.");
      setEdits((prev) => {
        const next = { ...prev };
        delete next[tpl.id];
        return next;
      });
      await loadItems();
      if (openTemplateId === tpl.id) {
        setOpenTemplateId("");
      }
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="space-y-4" data-testid="calendario-operational-reminders">
      <DespachoInfoCallout>
        Defina recordatorios por auxiliar (nóminas, revisiones internas, etc.): periodicidad, mensaje y destinatario.
        La precarga y el tablero de envío están en la sección plegable{" "}
        <strong>Recordatorios y alertas automáticas → Recordatorios programados → Actividades del despacho</strong>.
        Las tareas internas por empresa se gestionan en <strong>Tareas → Internas</strong>.
        La bandeja de mensajes al cliente está en <strong>Recordatorios a Clientes → Bandeja de envío</strong>.
      </DespachoInfoCallout>

      {!readOnly ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm"
            disabled={Boolean(draftNew)}
            onClick={() => setDraftNew({ ...EMPTY_TEMPLATE, _key: Date.now() })}
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden />
            Nuevo recordatorio
          </Button>
        </div>
      ) : null}

      {loadState === "loading" || auxLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando plantillas…
        </div>
      ) : null}

      {draftNew ? (
        <DespachoSectionCard title="Nuevo recordatorio operativo">
          <TemplateEditor
            template={draftNew}
            auxiliares={auxiliares}
            readOnly={readOnly}
            saving={savingId === "__new__"}
            deleting={false}
            onChange={(patch) => setDraftNew((prev) => ({ ...prev, ...patch }))}
            onSave={saveTemplate}
            onDelete={() => setDraftNew(null)}
          />
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mt-2 rounded-sm text-slate-600"
              onClick={() => setDraftNew(null)}
            >
              Cancelar
            </Button>
          ) : null}
        </DespachoSectionCard>
      ) : null}

      {displayItems.map((tpl) => (
        <AccordionSectionCard
          key={tpl.id}
          title={tpl.label || "Sin nombre"}
          description={`${recipientDisplayName(tpl)} · ${recurrenceSummary(tpl)}`}
          isOpen={openTemplateId === tpl.id}
          onToggle={() => setOpenTemplateId((current) => (current === tpl.id ? "" : tpl.id))}
        >
          <TemplateEditor
            template={tpl}
            auxiliares={auxiliares}
            readOnly={readOnly}
            saving={savingId === tpl.id}
            deleting={deletingId === tpl.id}
            onChange={(patch) => patchEdit(tpl.id, patch)}
            onSave={saveTemplate}
            onDelete={deleteTemplate}
          />
        </AccordionSectionCard>
      ))}

      {loadState === "ready" && !displayItems.length && !draftNew ? (
        <p className="text-sm text-slate-600 text-center py-6">
          No hay recordatorios operativos configurados. Agregue uno para avisar a cada auxiliar en fechas fijas.
        </p>
      ) : null}
    </div>
  );
}
